import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Typography,
  message,
  Switch,
  Row,
  Col,
  Select
} from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import clientApi from "../../clients/store/api";

const { Title, Text } = Typography;

const AdminEntitlementDetail = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entitlement, setEntitlement] = useState(null);
  const history = useHistory();
  const { id } = useParams();

  const isEditing = id && id !== "new";
  const isLoading = loading && isEditing;

  useEffect(() => {
    if (isEditing && id) {
      fetchEntitlement();
    }
  }, [id]);

  const fetchEntitlement = async () => {
    try {
      setLoading(true);
      const entitlementData = await clientApi.getEntitlementById(id);

      setEntitlement(entitlementData);
      form.setFieldsValue({
        name: entitlementData.name,
        description: entitlementData.description,
        cost: entitlementData.cost,
        term: entitlementData.term,
        period: entitlementData.period || "each",
        isActive: entitlementData.isActive,
        implementationNotes: entitlementData.implementationNotes
      });
    } catch (error) {
      console.error("Error fetching entitlement:", error);
      message.error("Failed to load entitlement");
      history.push("/admin/billing/entitlements");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);

      if (isEditing) {
        await clientApi.updateEntitlement(id, values);
        message.success("Entitlement updated successfully");
      } else {
        await clientApi.createEntitlement(values);
        message.success("Entitlement created successfully");
      }

      history.push("/admin/billing/entitlements");
    } catch (error) {
      console.error("Error saving entitlement:", error);
      message.error(`Failed to ${isEditing ? "update" : "create"} entitlement`);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    history.push("/admin/billing/entitlements");
  };

  return (
    <div className="admin-entitlement-detail">
      <Card loading={isLoading}>
        <div style={{ marginBottom: "24px" }}>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            style={{ padding: 0, height: "auto", marginBottom: "16px" }}
          >
            Back to Entitlements
          </Button>

          <Title level={3} style={{ margin: 0 }}>
            {isEditing ? "Edit Entitlement" : "Create New Entitlement"}
          </Title>

          {isEditing && entitlement && (
            <Text type="secondary">ID: {entitlement.simpleId}</Text>
          )}
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            isActive: true,
            period: "each"
          }}
        >
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  { required: true, message: "Please enter entitlement name" },
                  { max: 255, message: "Name must not exceed 255 characters" }
                ]}
              >
                <Input placeholder="e.g., SMS Notifications" size="large" />
              </Form.Item>

              <Form.Item
                name="description"
                label="Description"
                rules={[
                  { max: 1000, message: "Description must not exceed 1000 characters" }
                ]}
              >
                <Input.TextArea
                  placeholder="Describe what this entitlement provides..."
                  rows={4}
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="cost"
                label="Cost"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === null || value === undefined) {
                        return Promise.reject(new Error("Please enter a cost"));
                      }
                      if (value < 0) {
                        return Promise.reject(new Error("Cost cannot be negative"));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <InputNumber
                  placeholder="0.00"
                  min={0}
                  step={0.0001}
                  precision={4}
                  size="large"
                  style={{ width: "100%" }}
                  addonBefore="$"
                />
              </Form.Item>

              <Form.Item
                name="period"
                label="Billing Period"
                rules={[{ required: true, message: "Please select a billing period" }]}
              >
                <Select size="large" placeholder="Select billing period">
                  <Select.Option value="each">Each</Select.Option>
                  <Select.Option value="daily">Daily</Select.Option>
                  <Select.Option value="weekly">Weekly</Select.Option>
                  <Select.Option value="monthly">Monthly</Select.Option>
                  <Select.Option value="yearly">Yearly</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item name="isActive" label="Status" valuePropName="checked">
                <Switch
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                  size="default"
                />
              </Form.Item>
            </Col>

            <Col xs={24} lg={12}>
              <Form.Item
                name="implementationNotes"
                label="Implementation Notes"
                rules={[
                  {
                    max: 5000,
                    message: "Implementation notes must not exceed 5000 characters"
                  }
                ]}
              >
                <Input.TextArea
                  placeholder="Add implementation notes, technical details, or special instructions..."
                  rows={20}
                  size="large"
                  style={{ minHeight: "400px" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginTop: "32px", display: "flex", gap: "12px" }}>
            <Button onClick={handleBack} size="large">
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              icon={<SaveOutlined />}
              size="large"
            >
              {isEditing ? "Update Entitlement" : "Create Entitlement"}
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default AdminEntitlementDetail;
